// Kiểu dữ liệu cho tính năng Sơ đồ tư duy (Mindmap) — hạt nhân kiến thức.

/** Chế độ hiển thị mindmap khi gắn vào bài tập (dùng ở Phase 2 — deep-workspace). */
export type MindmapViewMode =
  | 'before'       // xem trước khi làm
  | 'during'       // xem trong khi làm
  | 'after'        // chỉ xem sau khi có kết quả
  | 'blind_build'  // học sinh tự dựng lại rồi đối chiếu
  | 'fill_blank';  // điền các node bị ẩn

export interface MindmapNode {
  id: string;
  label: string;
  note?: string;
  color?: string;
  children?: MindmapNode[];
}

export interface Flashcard {
  id: string;
  nodeId: string;
  front: string;
  back: string;
  hint?: string | null;
}

/** Snapshot mindmap đính kèm vào bài tập (lưu trong edu_assignments.mindmaps). */
export interface MindmapAttachment {
  mindmapId: string;
  title: string;
  viewMode: MindmapViewMode;
  hiddenNodeIds?: string[];
  tree: MindmapNode;
}

export const VIEW_MODE_LABELS: Record<MindmapViewMode, string> = {
  before: 'Xem trước khi làm',
  during: 'Xem trong khi làm',
  after: 'Chỉ xem sau khi có kết quả',
  blind_build: 'Tự dựng lại rồi đối chiếu',
  fill_blank: 'Điền vào ô trống (che nhánh)',
};

export type MindmapSourceType = 'manual' | 'ai' | 'from_content';

export interface Mindmap {
  id?: string;
  title: string;
  subject?: string;
  gradeLevel?: string;
  topic?: string;
  description?: string;
  sourceType: MindmapSourceType;
  sourceContentType?: string | null;
  sourceContentId?: string | null;
  nodeCount?: number;
  tree: MindmapNode;
  flashcards?: Flashcard[];
  status?: 'draft' | 'published';
  visibility?: string;
  createdAt?: string;
  updatedAt?: string;
}

/** Kết quả trả về từ endpoint sinh mindmap bằng AI. */
export interface GeneratedMindmap {
  title: string;
  summary?: string;
  tree: MindmapNode;
  nodeCount: number;
  sourceType: MindmapSourceType;
  cost?: number;
  balance?: number;
}

export interface GenerateMindmapParams {
  subject?: string;
  grade?: string;
  topic?: string;
  audience?: 'primary' | 'secondary' | 'highschool' | 'university';
  depth?: number;
  maxChildren?: number;
  /** Nếu truyền nội dung → AI tóm tắt nội dung thành sơ đồ (luồng "từ nội dung"). */
  sourceContent?: string;
  sourceContentType?: string;
}

// ── Phase 3: Ôn tập ngắt quãng (spaced repetition) + node mastery ──────────────

export type ReviewRating = 'again' | 'hard' | 'good' | 'easy';
export type MasteryLabel = 'new' | 'weak' | 'learning' | 'good' | 'mastered';

export interface ReviewCard {
  id: string;
  nodeId: string;
  front: string;
  back: string;
  hint?: string | null;
  repetitions: number;
  mastery: number;
  isNew: boolean;
}

export interface ReviewStats {
  total: number;
  new: number;
  due: number;
  mastered: number;
}

export interface NodeMasteryEntry {
  score: number;
  label: MasteryLabel;
}

export interface ReviewQueue {
  mindmapId: string;
  title: string;
  cards: ReviewCard[];
  stats: ReviewStats;
  nodeMastery: Record<string, NodeMasteryEntry>;
}

export interface ReviewResult {
  cardId: string;
  rating: ReviewRating;
  repetitions: number;
  intervalDays: number;
  dueAt?: string;
  mastery: number;
  masteryLabel: MasteryLabel;
}
